import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Asset, Box, Button, Card, Typography, useThemeValue } from '@altermaind/web/ui';
import { useDialog } from '@/_shared/hooks/useDialog';
import { usePublicDocs } from '@/_shared/hooks/usePublicDocs';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { useCurrentTemplate } from '@/surveys/state/hooks';

export const UsefulInformation = () => {
  const t = useTranslations('surveys.usefullInformations');
  const { genericResponsiveValue } = useResponsiveStyle();
  const { openDialog: openDialogPeopleTutorial } = useDialog('surveysTutorial');
  const navigate = useNavigate();
  const { surveyId } = useParams();

  const gapXS = useThemeValue('space', 'xs');

  const openTutorial = () => {
    openDialogPeopleTutorial();
  };

  const { goToPublicsDocs } = usePublicDocs();
  const { isHardSkillSurvey, isSoftSkillSurvey } = useCurrentTemplate();

  const DOCS = useMemo(() => {
    if (isHardSkillSurvey) {
      return 'HARD_SKILLS';
    }
    if (isSoftSkillSurvey) {
      return 'SOFT_SKILLS';
    }
    return undefined;
  }, [isHardSkillSurvey, isSoftSkillSurvey]);

  const handleSimulation = () => {
    navigate(`/surveys/${surveyId}/simulate`);
  };

  return (
    <Box>
      <Typography variant='h3' mt='l' mb='s'>
        {t('title')}
      </Typography>
      <Box display='flex' flexDirection='column' style={{ gap: gapXS }}>
        <Card p='xs'>
          <Box
            alignItems='center'
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
            display='flex'
          >
            <Box flex='1' alignSelf='flex-start' mr='xs'>
              <Typography variant='h2/light' color='primary/text' fontSize='s'>
                {t('boxTutorial.title')}
              </Typography>
              <Typography variant='p3/light' fontSize='xs'>
                {t('boxTutorial.description')}
              </Typography>
              <Button
                onClick={openTutorial}
                variant='positive'
                mt='xs'
                p='xxs'
                height='m'
                mb={genericResponsiveValue({
                  breakPoints: { desktop: 'none' },
                  defaultValue: 's',
                })}
              >
                {t('boxTutorial.buttonLabel')}
              </Button>
            </Box>
            <Box>
              <Asset
                width={genericResponsiveValue({
                  breakPoints: { desktop: 'auto' },
                  defaultValue: '100%',
                })}
                height={genericResponsiveValue({
                  breakPoints: { desktop: '130px' },
                  defaultValue: 'auto',
                })}
                borderRadius='m'
                domain='images'
                name='peopleTutorialImage'
              />
            </Box>
          </Box>
        </Card>
        <Box
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
          width={genericResponsiveValue({
            breakPoints: { desktop: 'fit-content' },
            defaultValue: '100%',
          })}
          style={{ gap: gapXS }}
        >
          <Card p='xs' display='flex' flexDirection='column'>
            <Box
              display='flex'
              flexDirection='column'
              flex='1'
              justifyContent='space-between'
              px='xs'
            >
              <Box>
                <Typography variant='h2/light' color='primary/text' fontSize='s'>
                  {t('model.title')}
                </Typography>
                <Typography variant='p3/light' fontSize='xs'>
                  {t('model.description')}
                </Typography>
              </Box>
              <Button
                width='fit-content'
                onClick={() => goToPublicsDocs({ goTo: DOCS })}
                variant='positive'
                mt='xs'
                p='xxs'
                height='m'
              >
                {t('model.buttonLabel')}
              </Button>
            </Box>
          </Card>
          <Card p='xs' display='flex' flexDirection='column'>
            <Box
              display='flex'
              flexDirection='column'
              flex='1'
              justifyContent='space-between'
              px='xs'
            >
              <Box>
                <Typography variant='h2/light' color='primary/text' fontSize='s'>
                  {t('simulation.title')}
                </Typography>
                <Typography variant='p3/light' fontSize='xs'>
                  {t('simulation.description')}
                </Typography>
              </Box>
              <Button
                width='fit-content'
                onClick={handleSimulation}
                variant='positive'
                mt='xs'
                p='xxs'
                height='m'
              >
                {t('simulation.buttonLabel')}
              </Button>
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};
